import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { useApi } from '@fortem-monorepo/react-spa'
import { ClientPricing } from '@fortem-monorepo/web-sdk'
import { API } from '@fortem-monorepo/web-sdk'

import { SchemaType as ProductSchemaType } from '@/components/forms/AddProductToClientsForm/AddProductToClientsForm'
import { SchemaType as EmailSchemaType } from '@/components/forms/SendEmailsToClientsForm/SendEmailsToClientsForm'
import { toastApiError } from '@/lib/error-toast'

// --------------------
// CLIENTS
// --------------------

export const useUpdateClientMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: API.UpdateBody) => {
      return api.clientPricing.updateClient(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Client updated successfully')
    },
  })
}

export const useCreateClientMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.CreateClientBody) => {
      return api.clientPricing.createClient(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Client created successfully')
    },
  })
}

// --------------------
// PRODUCTS
// --------------------

export const useUpdateProductMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: API.UpdateBody) => {
      return api.clientPricing.updateProduct(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product updated successfully')
    },
  })
}

export const useCreateProductMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.CreateProductBody) => {
      return api.clientPricing.createProduct(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product created successfully')
    },
  })
}

/**
 * Add a product to a specific client
 */
export const useAddProductToClientMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.AddProductToClientBody) => {
      return api.clientPricing.addProductToClient(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product added to client')
    },
  })
}

/**
 * Add a product to multiple clients
 */
export const useAddProductToClientsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: ({ client_ids, product_id }: ProductSchemaType) => {
      return Promise.all(
        client_ids.map((client_id) =>
          api.clientPricing.addProductToClient({ client_id, product_id }),
        ),
      )
    },
    onSuccess: (_data, params) => {
      toast.success(`Product added to ${params.client_ids.length} clients`)
    },
    onError: (error, params) => {
      toastApiError(error, `Error adding product to ${params.client_ids.length} clients`)
    },
  })
}

export const useDeleteProductFromClientMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.AddProductToClientBody) => {
      return api.clientPricing.deleteProductFromClient(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product removed from client')
    },
  })
}

export const useDeleteProductMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.DeleteProductBody) => {
      return api.clientPricing.deleteProduct(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product deleted successfully')
    },
  })
}

export const useDeleteProductMappingsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.DeleteProductBody) => {
      return api.clientPricing.deleteProductMappings(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Product mappings deleted successfully')
    },
  })
}

// --------------------
// EMAILS
// --------------------

/**
 * Send email to a single client
 */
export const useSendEmailToClientMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: (body: ClientPricing.SendEmailBody) => {
      return api.clientPricing.sendEmailToClient(body)
    },
    onSuccess: (_data, _params) => {
      toast.success('Email sent successfully')
    },
  })
}

/**
 * Send emails to multiple clients
 */
export const useSendEmailsToClientsMutation = () => {
  const api = useApi()

  return useMutation({
    mutationFn: ({ client_names, value_date }: EmailSchemaType) => {
      return Promise.all(
        client_names.map((client_name) =>
          api.clientPricing.sendEmailToClient({ client_name, value_date }),
        ),
      )
    },
    onSuccess: (_data, params) => {
      toast.success(
        `Emails sent to ${params.client_names.length > 5 ? `${params.client_names.length} clients` : params.client_names.join(', ')}`,
      )
    },
    onError: (error, params) => {
      toastApiError(
        error,
        `Error sending emails to ${params.client_names.length > 5 ? `${params.client_names.length} clients` : params.client_names.join(', ')}`,
      )
    },
  })
}

// -----------------------------------

export const useInvalidateClients = () => {
  const queryClient = useQueryClient()

  return () => {
    queryClient.invalidateQueries({ queryKey: ['client-pricing', 'clients', 'new'] })
  }
}

export const useInvalidateProducts = (flush?: boolean) => {
  const queryClient = useQueryClient()

  return (client_id?: number) => {
    queryClient.invalidateQueries({
      queryKey: flush
        ? ['client-pricing', 'products', 'new']
        : ['client-pricing', 'products', 'new', { client_id }],
    })
  }
}

export const v2 = {
  useUpdateClientMutation: () => {
    const api = useApi()
    const invalidateClients = useInvalidateClients()

    return useMutation({
      mutationFn: (body: ClientPricing.V2.UpdateClientParams) => {
        return api.clientPricing.v2.updateClient(body)
      },
      onSuccess: (_data, _params) => {
        toast.success('Client updated successfully')
      },
      onSettled: () => {
        invalidateClients()
      },
    })
  },
  useAddProductToClient: () => {
    const api = useApi()
    const invalidateProducts = useInvalidateProducts()

    return useMutation({
      mutationFn: (params: ClientPricing.V2.AddProductToClientParams) => {
        return api.clientPricing.v2.addProductToClient(params)
      },
      onSuccess: (_data, _params) => {
        toast.success('Product added')
      },
      onSettled: (_data, _error, params) => {
        invalidateProducts(params.org_id)
      },
    })
  },
  useAddProductToClients: () => {
    const api = useApi()
    const invalidateProducts = useInvalidateProducts(true)

    return useMutation({
      mutationFn: ({ org_ids, instrument_id }: { org_ids: number[]; instrument_id: number }) => {
        return Promise.all(
          org_ids.map((org_id) =>
            api.clientPricing.v2.addProductToClient({ org_id, instrument_id }),
          ),
        )
      },
      onSuccess: (_data, params) => {
        toast.success(`Product added to ${params.org_ids.length} clients`)
      },
      onError: (error, params) => {
        toastApiError(error, `Error adding product to ${params.org_ids.length} clients`)
      },
      onSettled: (_data, _error, _params) => {
        invalidateProducts()
      },
    })
  },
  useRemoveProductFromClient: () => {
    const api = useApi()
    const invalidateProducts = useInvalidateProducts()

    return useMutation({
      mutationFn: (params: ClientPricing.V2.RemoveProductFromClientParams) => {
        return api.clientPricing.v2.removeProductFromClient(params)
      },
      onSuccess: (_data, _params) => {
        toast.success('Product removed')
      },
      onSettled: (_data, _error, params) => {
        invalidateProducts(params.org_id)
      },
    })
  },
  useRemoveProductFromAllClients: () => {
    const api = useApi()
    const invalidateProducts = useInvalidateProducts(true)

    return useMutation({
      mutationFn: (params: { instrument_id: number }) => {
        return api.clientPricing.v2.deleteProductMappings(params)
      },
      onSuccess: (_data, _params) => {
        toast.success('Product removed')
      },
      onSettled: (_data, _error, _params) => {
        invalidateProducts()
      },
    })
  },
  sendEmailToClient: () => {
    const api = useApi()

    return useMutation({
      mutationFn: (params: ClientPricing.V2.SendEmailToClientParams) => {
        return api.clientPricing.v2.sendEmailToClients(params)
      },
      onSuccess: (_data, _params) => {
        toast.success(`Email sent`)
      },
    })
  },
  sendEmailToClients: () => {
    const api = useApi()

    return useMutation({
      mutationFn: (params: ClientPricing.V2.SendEmailToClientParams) => {
        return api.clientPricing.v2.sendEmailToClients(params)
      },
      onSuccess: (_data, _params) => {
        toast.success(`Email sent`)
      },
    })
  },
}
